import React, { FC, useEffect, useState } from "react";
import { api } from "@/helper/api";
import { Grid, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "@/components/permissions/permission.module.css";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

interface Props {
  title?: string;
  group?: any;
}

const PermissionGroupTable: FC<Props> = () => {
  const [allPermissions, setAllPermissions] = useState<any>([]);
  const [groupName, setGroupName] = useState<string>("");
  const [groupPermissions, setGroupPermissions] = useState<any>([]);
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const { t } = useTranslation();
  usePageHeading(t("permissions"));

  const { groupId } = useParams() as {
    groupId: string;
  };
  const { showConfirmationModalAsync } = useConfirmationModal();

  const navigate = useNavigate();

  useEffect(() => {
    getAllPermissions();
  }, []);

  useEffect(() => {
    if (groupId) {
      api.genericApiRequest({
        entity: "group",
        method: "get",
        entityId: groupId,
        successHandler: (res: any) => {
          const data = res.data;
          setGroupPermissions(data.permissions);
          setGroupName(data.name);
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  }, [groupId]);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const getAllPermissions = () => {
    let itemCollection: any = [];
    const additionalUrlParameters: any = {};

    additionalUrlParameters["depth"] = "0";
    additionalUrlParameters["limit"] = "50";

    api.genericApiRequest({
      entity: "permission",
      method: "get",
      getAllPages: true,
      successHandler: (res: any) => {
        itemCollection = res.data.results;
        setAllPermissions(
          itemCollection.sort(
            (a: any, b: any) => a.content_type - b.content_type
          )
        );
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      parametersToRender: additionalUrlParameters,
    });
  };

  const handleCheckboxChange = (isChecked: boolean, permissionName: any) => {
    if (isChecked) {
      if (!groupPermissions.includes(permissionName)) {
        setGroupPermissions((prevPermissions: any) => [
          ...prevPermissions,
          permissionName,
        ]);
      }
    } else {
      setGroupPermissions((prevPermissions: any) =>
        prevPermissions.filter((name: any) => name !== permissionName)
      );
    }
  };

  const hanldeUpdateGroup = async (skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("confirm_save_new_grouppermissions", { group: groupName })
      );
      if (!isConfirmed) return;
    }
    const result = { name: groupName, permissions: groupPermissions };
    const additionalUrlParameters: any = { include_kaer_internal: true };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "group",
      method: "put",
      parametersToRender: additionalUrlParameters,
      submitData: result,
      config: config,
      entityId: groupId,
      successHandler: () => {
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  let currentContentType: any = null;

  const handleNavigateBack = () => {
    navigate("/settings/permissionGroupList");
  };
  return (
    <>
      <div className={styles.permissionWrapper}>
        <Tooltip placement="right" title={t("back")}>
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={handleNavigateBack}
          >
            <ArrowBackIcon />
          </GenericButton>
        </Tooltip>
        <Grid container className={styles.permissionsContainer}>
          {groupId !== "" && (
            <Grid item xs={12}>
              <Grid item xs={12}>
                <h2>
                  {t(groupName)} ({groupName})
                </h2>
              </Grid>
              <table className={styles.permissionTable}>
                <tbody>
                  {allPermissions.map((permission: any) => {
                    const { name, content_type, id, codename } = permission;
                    const isNewContentType =
                      content_type !== currentContentType;
                    currentContentType = content_type;
                    return (
                      <React.Fragment key={id}>
                        {isNewContentType && (
                          <tr className="permissionSectionHeading">
                            <th>{content_type}</th>
                            <th>{t("permitted")}</th>
                          </tr>
                        )}
                        <tr>
                          <td>{name}</td>
                          <td>
                            <input
                              type="checkbox"
                              name={codename}
                              checked={groupPermissions.includes(codename)}
                              onChange={(event: any) => {
                                handleCheckboxChange(
                                  event.target.checked,
                                  codename
                                );
                              }}
                            />
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </Grid>
          )}
          {groupId !== "" && (
            <section className={styles.buttonsContainer}>
              <GenericButton variant="outlined" onClick={handleNavigateBack}>
                {t("back")}
              </GenericButton>
              <GenericButton onClick={() => hanldeUpdateGroup(false)}>
                {t("save")}
              </GenericButton>
            </section>
          )}
        </Grid>
      </div>
      {errorModal}

      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default PermissionGroupTable;
